"use client"

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import { extractBlockProperties } from "@/utils/simplifyData"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import dynamic from "next/dynamic"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { useInViewConfig, useIsDesktop, useTransitionUpStyles } from "ui"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getNonUmbracoUrl } from "utils"
import BlockWrapper from "../common/BlockWrapper"
import CommonTitle from "../common/bit/CommonTitle"

// Dynamically import VideoContainer to disable SSR
const VideoContainer = dynamic(() => import("./VideoContainer"), { ssr: false })

interface ArticleContentProps {
  data: any
  id?: string
}

const StyledContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "inView",
})<{ inView: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  opacity: ${({ inView }) => (inView ? 1 : 0)};
  transform: ${({ inView }) => (inView ? "translateY(0)" : "translateY(20px)")};
  transition:
    opacity 0.5s ease-in-out,
    transform 0.5s ease-in-out;
  gap: 80px;
  ${mediaQueries.tabletDown} {
    padding-top: 40px;
  }
`

const StyledTitlesWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: ${sizes.baseDouble};
  margin-bottom: ${sizes.baseSingle};
`
const StylesTextsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledTitle = styled(CommonTitle)`
  ${mediaQueries.tabletDown} {
    margin-top: 0;
  }
  h2 {
    font-size: 25px;
    text-align: start;

    ${mediaQueries.desktopM} {
      font-size: 22px;
    }
  }
`

const StyledParagraph = styled(Typography)<{ component?: string }>`
  color: ${colors.black};
  line-height: 1.6;
  a {
    color: ${colors.black};
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
  > p {
    margin: 0;
    font-size: 16px;
    ${mediaQueries.desktopM} {
      font-size: 18px;
    }
  }
`

const StyledImage = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${mediaQueries.tabletDown} {
    order: 2;
  }
`

const StyledNextImage = styled(Image)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${sizes.borderRadius};
`

const StyledQuote = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${sizes.baseTriple} ${sizes.baseSix};
  background-color: ${colors.white};
  border-radius: ${sizes.borderRadiusLarge};
  height: fit-content;
  max-width: 404px;
  p {
    color: ${colors.red};
    font-size: 22px;
    font-weight: 700;
    line-height: 180%;
  }
  ${mediaQueries.tabletDown} {
    order: -1;
  }
`
const NumberedItemContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  ${mediaQueries.tabletDown} {
    width: 100%;
  }
`

const NumberCircle = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${sizes.baseQuadric};
  height: ${sizes.baseQuadric};
  border-radius: 50%;
  background-color: ${colors.red};
  color: ${colors.white};
  font-size: 20px;
  font-weight: 700;
  flex: 0 0 auto;
`

const StyledItemContent = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${sizes.baseQuadric};
  ${mediaQueries.tabletDown} {
    flex-direction: column;
    align-items: center;
  }
`

const ArticleContent = ({ data, id }: ArticleContentProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const parsedItemsList = extractBlockProperties(data?.itemsList?.blocks)
  const isDesktop = useIsDesktop()

  const renderContent = () => {
    if (!parsedItemsList) return null

    return parsedItemsList.map((item: any, index: number) => {
      const key = `article-item-${index}`
      if (!item) return null
      const { title, number, text, redText, media, imageSmall } = item
      return (
        <NumberedItemContainer key={key}>
          <StyledItemContent>
            {imageSmall && (
              <StyledImage>
                {imageSmall.src && (
                  <StyledNextImage
                    src={imageSmall.src}
                    alt={imageSmall.alt || title?.value || "Article image"}
                    width={isDesktop ? 400 : 380}
                    height={isDesktop ? 264 : 262}
                    priority={index === 0} // Load first image with priority
                  />
                )}
              </StyledImage>
            )}
            <StylesTextsWrapper>
              <StyledTitlesWrapper>
                {!!number && number !== 0 && (
                  <NumberCircle>{number} </NumberCircle>
                )}
                {title && <StyledTitle title={title} />}
              </StyledTitlesWrapper>
              {text && (
                <StyledParagraph
                  variant="body1"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(text),
                  }}
                />
              )}
            </StylesTextsWrapper>
            {redText && (
              <StyledQuote>
                <Typography
                  variant="body1"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(redText),
                  }}
                />
              </StyledQuote>
            )}
          </StyledItemContent>
          {media && (
            <StyledImage>
              {media.type === "VIDEO" ? (
                <VideoContainer
                  url={getNonUmbracoUrl(media.src)}
                  title={title?.value}
                />
              ) : (
                media.src && (
                  <StyledNextImage
                    src={media.src}
                    alt={media.alt || title?.value || "Article image"}
                    width={isDesktop ? 850 : 380}
                    height={isDesktop ? 460 : 249}
                    priority={index === 0} // Load first image with priority
                  />
                )
              )}
            </StyledImage>
          )}
        </NumberedItemContainer>
      )
    })
  }

  return (
    <BlockWrapper id={id}>
      <StyledContent ref={ref} style={transitionStyles} inView={inView}>
        {renderContent()}
      </StyledContent>
    </BlockWrapper>
  )
}

export default ArticleContent
